import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Logo } from "@/components/Logo";
import { LEGAL, LEGAL_PATHS } from "@/lib/legal";

const Body = ({ doc }) => (
  <div className="space-y-5">
    {doc.sections.map(([h, ps], i) => (
      <section key={i} className="space-y-2">
        {h && <h2 className="font-display text-xl font-bold text-[#FACC15]">{h}</h2>}
        {ps.map((p, j) => <p key={j} className="text-slate-300 leading-relaxed">{p}</p>)}
      </section>
    ))}
  </div>
);

export const LegalPage = ({ kind }) => {
  const doc = LEGAL[kind];
  return (
    <div className="min-h-[100dvh] px-5 py-10" data-testid={`legal-page-${kind}`}>
      <div className="max-w-3xl mx-auto space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white" data-testid="legal-back-link"><ArrowLeft className="h-4 w-4" />Ana sayfa</Link>
        <Logo />
        <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase leading-tight">{doc.title}</h1>
        <div className="panel p-6 sm:p-8"><Body doc={doc} /></div>
      </div>
    </div>
  );
};

export const LegalLink = ({ kind, children }) => (
  <Link to={LEGAL_PATHS[kind]} target="_blank" className="text-[#FACC15] underline underline-offset-2 hover:text-white" onClick={(e) => e.stopPropagation()} data-testid={`legal-link-${kind}`}>{children || LEGAL[kind].short}</Link>
);

export const EthicsLink = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="text-[#FACC15] underline underline-offset-2 hover:text-white" onClick={(e) => { e.preventDefault(); setOpen(true); }} data-testid="ethics-link">FChem Akademik Dürüstlük ve Topluluk Kuralları</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ethics-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{LEGAL.ethics.title}</DialogTitle></DialogHeader>
          <Body doc={LEGAL.ethics} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ConsentCheck = ({ checked, onChange, children, testId }) => (
  <label className="flex items-start gap-3 text-sm text-slate-300 leading-snug cursor-pointer" data-testid={testId}>
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(Boolean(v))} className="mt-0.5" data-testid={`${testId}-checkbox`} />
    <span>{children}</span>
  </label>
);

export const ROOM_CONSENT = "Bu odaya katılacak öğrencilerimin FChem platformunu eğitim amacıyla kullanması için gerekli pedagojik onayın ve yasal (veli) izinlerinin tarafımca/okulumuzca sağlandığını kabul ediyorum.";

const HIDDEN = ["/tahta/", "/onizleme", "/oyun/"];
const PANEL = (p) => p.startsWith("/ogretmen/") && !p.startsWith("/ogretmen/giris") && !p.startsWith("/ogretmen/kayit");

export const SiteFooter = ({ inPanel = false, signature = false }) => {
  const { pathname } = useLocation();
  if (HIDDEN.some((p) => pathname.startsWith(p)) || (!inPanel && (PANEL(pathname) || pathname === "/"))) return null;
  return (
    <footer className={`${inPanel ? "mt-10 pt-4 border-t border-white/10" : "px-5 py-5 border-t border-white/10"} ${signature ? "!mt-0 !pt-2 !border-0" : ""} text-xs text-slate-400 flex flex-col items-center gap-1.5`} data-testid="site-footer">
      {signature && (
        <div className="flex flex-wrap items-center justify-center gap-x-4" data-testid="footer-signature">
          <span>Furkan Buyrukbilen</span><a href="mailto:hi@furkan.agency" className="hover:text-[#FACC15]" data-testid="home-signature-email">hi@furkan.agency</a>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
        <span>© 2026</span>
        {["terms", "privacy", "kvkk"].map((k) => <Link key={k} to={LEGAL_PATHS[k]} className="whitespace-nowrap hover:text-white underline-offset-2 hover:underline" data-testid={`footer-link-${k}`}>{LEGAL[k].short}</Link>)}
      </div>
    </footer>
  );
};

const COOKIE_KEY = "fchem_cookie_ok";
export const CookieBanner = () => {
  const [ok, setOk] = useState(() => Boolean(localStorage.getItem(COOKIE_KEY)));
  if (ok) return null;
  return (
    <div className="fixed bottom-3 inset-x-3 z-[60] mx-auto max-w-3xl panel !rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3 shadow-2xl" data-testid="cookie-banner">
      <Cookie className="h-5 w-5 text-[#FACC15] shrink-0" />
      <p className="text-sm text-slate-200 flex-1 min-w-[200px]">Sitemizde deneyiminizi geliştirmek için çerezler kullanıyoruz. Detaylı bilgi için <LegalLink kind="privacy" />'nı inceleyebilirsiniz.</p>
      <Button size="sm" onClick={() => { localStorage.setItem(COOKIE_KEY, "1"); setOk(true); }} data-testid="cookie-accept">Kabul Et</Button>
    </div>
  );
};
